import React, { useContext, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import {
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyDoc, propertyCollection } from "../../utils/firestorePaths";
import { drawReportHeader, stampPageNumbers } from "../../utils/reports/pdfChrome";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";

/**
 * Daily Operations Summary — a current snapshot: room status counts, rooms
 * brought up today, open work orders, open issues, and today's MOD completion.
 */
export default function GenerateDailyOpsSummary({ user }) {
  const { selectedProperty, selectedClient, rooms } = useContext(PropertyContext);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const today = format(new Date(), "yyyy-MM-dd");

      // Room status counts (from context)
      const statusCounts = {};
      (rooms || []).forEach((r) => {
        const s = r.status || "Unknown";
        statusCounts[s] = (statusCounts[s] || 0) + 1;
      });

      // Parallel fetches
      const [historySnap, woSnap, issuesSnap, modSnap] = await Promise.all([
        getDoc(
          propertyDoc(selectedClient, selectedProperty, "propertyActionHistory", today)
        ),
        getDocs(
          query(
            propertyCollection(selectedClient, selectedProperty, "workOrders"),
            where("completed", "==", false)
          )
        ),
        getDocs(
          query(
            propertyCollection(selectedClient, selectedProperty, "propertyIssues"),
            where("completed", "==", false)
          )
        ),
        getDocs(
          propertyCollection(
            selectedClient,
            selectedProperty,
            "modTasks",
            "dailyReports",
            today
          )
        ),
      ]);

      const broughtUpToday = historySnap.exists()
        ? (historySnap.data().actions || []).filter((a) => a.newStatus === "Up").length
        : 0;
      const openWO = woSnap.size;
      const openIssues = issuesSnap.size;
      const modTotal = modSnap.size;
      const modCompleted = modSnap.docs.filter((d) => {
        const t = d.data();
        return !!(t.markedCompletedTime || t.markedCompleted || t.completed);
      }).length;
      const modPct = modTotal > 0 ? `${Math.round((modCompleted / modTotal) * 100)}%` : "—";

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const contentTop = drawReportHeader(pdf, {
        title: "Daily Operations Summary",
        dateRange: format(new Date(), "MMM d, yyyy"),
        generatedBy: user?.fullName || user?.personalEmail || "Unknown",
      });

      // Key metrics table
      autoTable(pdf, {
        startY: contentTop,
        head: [["Metric", "Value"]],
        body: [
          ["Total rooms", String((rooms || []).length)],
          ["Rooms brought up today", String(broughtUpToday)],
          ["Open work orders", String(openWO)],
          ["Open property issues", String(openIssues)],
          ["MOD tasks completed today", `${modCompleted} / ${modTotal} (${modPct})`],
        ],
        styles: { font: "helvetica", fontSize: 11, cellPadding: 3 },
        headStyles: { fillColor: [40, 40, 40], textColor: 255 },
        columnStyles: { 1: { halign: "right" } },
        margin: { left: 12, right: 12 },
        theme: "striped",
      });

      // Room status breakdown
      const statusRows = Object.entries(statusCounts).sort((a, b) => b[1] - a[1]);
      autoTable(pdf, {
        startY: pdf.lastAutoTable.finalY + 8,
        head: [["Room Status", "Count"]],
        body: statusRows.map(([s, c]) => [s, String(c)]),
        styles: { font: "helvetica", fontSize: 11, cellPadding: 3 },
        headStyles: { fillColor: [60, 60, 60], textColor: 255 },
        columnStyles: { 1: { halign: "right" } },
        margin: { left: 12, right: 12 },
        theme: "grid",
      });

      stampPageNumbers(pdf);
      downloadBlob(
        pdf.output("blob"),
        buildReportFilename("Daily_Operations_Summary", { ext: "pdf" })
      );
    } catch (e) {
      console.error("Error generating Daily Operations Summary:", e);
      alert("Error generating report. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      fullWidth
      onClick={generate}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={16} /> : null}
    >
      {loading ? "Generating..." : "Daily Operations Summary"}
    </Button>
  );
}
